import { useEffect, useState } from 'react'
import Select from 'react-select'
import Swal from 'sweetalert2'

import { IsGranted, ROLE, useAuth } from '../../../auth'
import { SelectStyles } from '../../../../../components/Select'
import { useThemeMode } from '../../../../../_metronic/partials'
import { KTIcon } from '../../../../../_metronic/helpers'
import { getMerchantActiveCurrency, setMerchantActiveCurrency } from './api'
import { CurrencyOption, MerchantCurrency } from './types'
import { getErrorStatus } from '../../../../common/api/errors'

const OPTIONS: CurrencyOption[] = [
  { value: 'USDT', label: 'USDT' },
  { value: 'RUB', label: 'RUB' },
]

export const MerchantActiveCurrency = ({ merchantId }: { merchantId: number }) => {
  const { mode: theme } = useThemeMode()
  const { currentUser } = useAuth()
  const canEdit = IsGranted([ROLE.ROLE_ADMIN, ROLE.ROLE_MANAGER, ROLE.ROLE_MERCHANT_MANAGER], currentUser)

  const [initial, setInitial] = useState<MerchantCurrency | null>(null)
  const [selected, setSelected] = useState<MerchantCurrency | null>(null)
  const [loading, setLoading] = useState(true)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    let active = true
    getMerchantActiveCurrency(merchantId)
      .then((res) => {
        if (!active) return
        if (res.data?.success && res.data.data) {
          setInitial(res.data.data.currency)
          setSelected(res.data.data.currency)
        } else {
          setFailed(true)
        }
      })
      .catch(() => active && setFailed(true))
      .finally(() => active && setLoading(false))
    return () => {
      active = false
    }
  }, [merchantId])

  const dirty = selected !== null && selected !== initial

  const apply = async () => {
    if (!selected) return
    const result = await Swal.fire({
      titleText: `Сменить активную валюту мерчанта на ${selected}?`,
      text: 'Новые операции пойдут через кошелёк выбранной валюты. Историю и баланс старого кошелька это не затрагивает.',
      icon: 'warning',
      showCancelButton: true,
      cancelButtonText: 'Отмена',
      confirmButtonText: 'Сменить',
      showLoaderOnConfirm: true,
      preConfirm: async () => {
        try {
          const res = await setMerchantActiveCurrency(merchantId, selected)
          if (!res.data?.success || !res.data.data) {
            throw new Error('set_failed')
          }
          return res.data.data.currency
        } catch (e: any) {
          const status = getErrorStatus(e)
          const msg =
            status === 409
              ? 'Смена запрещена: на текущем кошельке есть баланс или незавершённые операции'
              : status === 502 || status === 503
              ? 'Ledger недоступен, попробуйте позже'
              : 'Не удалось сменить валюту'
          Swal.close()
          Swal.fire({ toast: true, position: 'top', icon: 'error', title: msg, showConfirmButton: false, timer: 4000 })
          throw e
        }
      },
      allowOutsideClick: () => !Swal.isLoading(),
    })

    if (result.isConfirmed && typeof result.value === 'string') {
      const cur = result.value as MerchantCurrency
      setInitial(cur)
      setSelected(cur)
      Swal.fire({ toast: true, position: 'top', icon: 'success', title: 'Активная валюта обновлена', showConfirmButton: false, timer: 2500 })
    }
  }

  const reset = () => setSelected(initial)

  if (loading) return <span className='text-muted'>Загрузка…</span>
  if (failed) return <span className='text-danger'>Ledger недоступен</span>
  if (!canEdit) return <span className='fw-bold'>{initial}</span>

  return (
    <div className='d-flex align-items-center gap-2' style={{ maxWidth: 320 }}>
      <div style={{ minWidth: 160 }}>
        <Select
          options={OPTIONS}
          value={OPTIONS.find((o) => o.value === selected) ?? null}
          onChange={(opt) => setSelected((opt?.value as MerchantCurrency) ?? null)}
          isClearable={false}
          isSearchable={false}
          styles={SelectStyles(theme)}
        />
      </div>
      {dirty && (
        <>
          <button type='button' className='btn btn-sm btn-icon btn-light-success' title='Применить' onClick={apply}>
            <KTIcon iconName='check' className='fs-2' />
          </button>
          <button type='button' className='btn btn-sm btn-icon btn-light-danger' title='Сбросить' onClick={reset}>
            <KTIcon iconName='cross' className='fs-2' />
          </button>
        </>
      )}
    </div>
  )
}
